import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { FirebaseApi } from 'src/app/core/classes/firebase-api';
import { NotificationRole, NotificationTypes } from 'src/app/core/enums/notification-types.enum';
import { Notification as AppNotification } from 'src/app/core/interfaces/notification';
import { Global } from 'src/app/core/resources/global';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { AuthService } from 'src/app/core/services/authentication.service';
import { CargoService } from 'src/app/core/services/cargo.service';
import { RoleService } from 'src/app/core/services/role.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { GpsNotificationComponent } from 'src/app/shared/gps-notification/gps-notification.component';

@Injectable({
  providedIn: 'root'
})
export class LastNotificationListenerService {
  private unsubscribes: Array<() => void> = [];
  private listening = false;
  private readonly collectionName = 'LastNotification';

  constructor(
    private authService: AuthService,
    private cargoService: CargoService,
    private dialog: MatDialog,
    private global: Global,
    private roleService: RoleService,
    private snackBar: SnackBarService
  ) { }

  public start(): void {
    if (this.listening) return;
    if (!this.authService || !this.authService.sessionActive()) return;

    this.stop(false);
    this.subscribeByRole();
    this.listening = true;
  }

  public stop(clearGpsAlert: boolean = true): void {
    this.unsubscribes.forEach((unsubscribe) => unsubscribe());
    this.unsubscribes = [];
    this.listening = false;

    if (clearGpsAlert) {
      this.cargoService.emitGpsTrackingAlert(null);
    }
  }

  /**
   * @description Suscribe únicamente a los grupos de notificación que el rol del usuario tiene habilitados.
   * Los grupos se identifican con los UUID de NotificationRole almacenados en role.notificationTypes.
   */
  private subscribeByRole(): void {
    const role = this.roleService ? this.roleService.getRoleUser() : null;
    if (!role || !Array.isArray(role.notificationTypes)) return;

    const notifications = role.notificationTypes;

    if (notifications.includes(NotificationRole.GEOFENCE)) this.subscribeGeofence();
    if (notifications.includes(NotificationRole.PANIC)) this.subscribePanic();
    if (notifications.includes(NotificationRole.GPS_TRACKING_ALERT)) this.subscribeGpsTrackingAlert();
    if (notifications.includes(NotificationRole.ROUTE_DETOUR)) this.subscribeRouteDetour();
  }

  private subscribeGeofence(): void {
    this.subscribeToNotification(
      NotificationTypes.GEOFENCE,
      notification => this.createGeneralNotification(notification, '¡Notificación de geocerca!')
    );

    this.subscribeToNotification(
      NotificationTypes.DELAY_BETWEEN_POINTS,
      notification => this.createGeneralNotification(notification, '¡Carga creada!')
    );
  }

  private subscribePanic(): void {
    const panicTypes: NotificationTypes[] = [
      NotificationTypes.PANIC,
      NotificationTypes.GPS_OFF,
      NotificationTypes.ANTENNA,
      NotificationTypes.VEHICLE_OFF,
      NotificationTypes.LOW_BATTERY,
      NotificationTypes.OPENED_DOOR,
      NotificationTypes.TIME_REPORT_ALERT,
      NotificationTypes.HARD_BRAKING,
      NotificationTypes.SUDDEN_TURN,
      NotificationTypes.SUDDEN_ACCELERATION
    ];

    panicTypes.forEach((type) =>
      this.subscribeToNotification(type, notification => this.createPanicNotification(notification))
    );
  }

  private subscribeRouteDetour(): void {
    this.subscribeToNotification(
      NotificationTypes.ROUTE_DETOUR,
      notification => this.createGeneralNotification(notification, '¡Desvío de ruta!', undefined, true)
    );
  }

  /**
   * @description Crea un observador de tipo base de notificación.
   * @param type Tipo de notificación (id del documento en la colección LastNotification).
   * @param callback Callback que recibe la notificación como argumento.
   */
  private subscribeToNotification(type: NotificationTypes, callback?: (notification: AppNotification) => void): void {
    let isFirstTime = true;
    const ref = FirebaseApi.firestore.collection(this.collectionName).doc(type);

    const unsubscribe = ref.onSnapshot(
      (snapshot: firebase.firestore.DocumentSnapshot) => {
        if (isFirstTime) {
          isFirstTime = false;
          return;
        }

        const notification = snapshot && snapshot.exists ? (snapshot.data() as AppNotification) : null;
        if (!notification) return;

        if (callback) callback(notification);
      },
      (error) => {
        console.error(`[LastNotification] Error escuchando ${this.collectionName}/${type}`, error);
      }
    );

    this.unsubscribes.push(unsubscribe);
  }

  /**
   * @description Observador dedicado a la alerta de monitoreo GPS. A diferencia del resto de tipos,
   * refleja el estado actual del banner al cargar (valor inicial) y lo limpia solo cuando el documento
   * no existe. No se usa `state` como bandera (una alerta vigente puede llegar con state === false).
   * El push de navegador solo se dispara ante cambios reales, no en la emisión inicial, para no repetirlo
   * en cada recarga.
   */
  private subscribeGpsTrackingAlert(): void {
    let isFirstTime = true;
    const ref = FirebaseApi.firestore.collection(this.collectionName).doc(NotificationTypes.GPS_TRACKING_ALERT);

    const unsubscribe = ref.onSnapshot(
      (snapshot: firebase.firestore.DocumentSnapshot) => {
        const firstEmit = isFirstTime;
        isFirstTime = false;

        const notification = snapshot && snapshot.exists ? (snapshot.data() as AppNotification) : null;

        // El documento GPS no usa `state` como bandera de mostrar/ocultar: una alerta vigente puede
        // tener state === false. Solo se limpia el banner cuando el documento no existe.
        if (!notification) {
          this.cargoService.emitGpsTrackingAlert(null);
          return;
        }

        this.cargoService.emitGpsTrackingAlert(notification);
        if (!firstEmit) this.createGeneralNotification(notification, 'Alerta de GPS!', notification.description);
      },
      (error) => {
        console.error(`[LastNotification] Error escuchando ${this.collectionName}/${NotificationTypes.GPS_TRACKING_ALERT}`, error);
        this.cargoService.emitGpsTrackingAlert(null);
      }
    );

    this.unsubscribes.push(unsubscribe);
  }

  /**
   * @description Abre el popup crítico (GpsNotificationComponent) y genera el push de navegador.
   * @param data Notificación almacenada en la base de datos.
   * @param disableClose Impide cerrar el diálogo con backdrop/esc (por defecto true).
   */
  private createPanicNotification(data: AppNotification, disableClose: boolean = true) {
    const config = new MatDialogConfig();
    config.data = data;
    config.maxHeight = ModalEnum.MAX_HEIGHT;
    config.width = ModalEnum.LARGE_WIDTH;
    config.maxWidth = ModalEnum.MAX_WIDTH;
    config.autoFocus = false;
    config.disableClose = disableClose;

    const dialog = this.dialog.open(GpsNotificationComponent, config);
    this.createGeneralNotification(data, '¡Crítico!');
    return dialog;
  }

  /**
   * @description Crea / intenta crear un push de navegador.
   * @param data Notificación almacenada en la base de datos.
   * @param title Título del push.
   * @param description Texto opcional; si no se pasa se arma con la carga y su descripción.
   * @param requireInteraction Mantiene el push visible hasta que el usuario interactúe.
   */
  private createGeneralNotification(data: AppNotification, title: string, description?: string, requireInteraction: boolean = false): void {
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    Notification.requestPermission()
      .then((permission) => {
        if (permission !== 'granted') return;

        const img = this.global.pathImgLogoLoggiapp;
        const text = description || `Carga ${data.consecutive} - ${data.description}`;
        const options: NotificationOptions = { body: text, icon: img };
        if (requireInteraction) options.requireInteraction = true;

        new Notification(title, options);
      })
      .catch((error) => {
        console.error(error);
        this.snackBar.openSnackBar(ServiceMessages.CANNOT_SEND_NOTIFICATIONS, undefined, 'alert');
      });
  }
}
